/**
 * TaskPage(프로젝트 상세) 셸 플래그.
 *
 * ⑵차 좌측 '내 프로젝트' 패널 — **기본 ON**.
 * 근거: 레이아웃 SSOT `docs/all-frame.html` 3d(프로젝트 · 상세) —
 *   "좌측 2차 상세 = 내 프로젝트를 카드형으로 나열(진행/완료 · 진행률·기간·멤버)".
 *   - ON  : `ProjectSidePanel`(좌측 세로 카드 목록) 렌더. 목록 위치만 시안대로 이동한다.
 *   - OFF : 롤백 경로 — 기존 상단 가로 바(`ProjectTopBar`, v2.7) 그대로.
 *           `VITE_TASK_PROJECT_SIDEPANEL='0'` 로 끈다.
 *
 * 목록이 담는 정보·동작(선택/생성/삭제)과 본문(WBS 간트 · 칸반)은 양쪽 경로 모두 동일하다.
 */
export const TASK_PROJECT_SIDEPANEL = import.meta.env.VITE_TASK_PROJECT_SIDEPANEL !== '0';

/**
 * 팀 화면(/project/teams) 셸 — **기본 ON**.
 * 근거: design-20260724-project-team-scope D-4 A안(확정) — 내 프로젝트와 같은 문법으로
 *   좌측 = 내 팀 목록 · 본문 = 선택 팀(연결 프로젝트·팀원). 화면 전체를 쓰고 세로도 꽉 채운다.
 *   - ON  : 좌측 팀 패널 + 본문 2열 셸(폭 제한 없음, height 100%).
 *   - OFF : 롤백 경로 — 기존 1200px 가운데 정렬 + 상단 페이지 헤더 + 280px 그리드.
 *           `VITE_PROJECT_TEAMS_SHELL='0'` 로 끈다.
 */
export const PROJECT_TEAMS_SHELL = import.meta.env.VITE_PROJECT_TEAMS_SHELL !== '0';

/**
 * 3c 프로젝트 홈(`/project`) — **기본 ON**.
 * 근거: 레이아웃 SSOT `docs/all-frame.html` 05.프로젝트 3c(초기 화면) —
 *   좌측 2차 네비(내 팀·프로젝트·내 할일) + 위젯 4종(내 팀·주간 완료율·프로젝트·내가 하는 일).
 *   - ON  : 3c 셸 렌더. 프로젝트 메뉴의 착지 지점이 되며 여기서 3d(/task)로 내려간다.
 *   - OFF : 롤백 경로 — 기존 숫자 카드 대시보드 그대로. `VITE_PROJECT_HOME_3C='0'`.
 */
export const PROJECT_HOME_3C = import.meta.env.VITE_PROJECT_HOME_3C !== '0';

/**
 * AW-001 프로젝트메인 개선안(`/project`) — **기본 OFF (opt-in)**.
 * 근거: 화면설계서 v2.2 `AW-001` (프로젝트 > 내 팀, Page No.1) — 상사 리뷰 완료분.
 *   히어로(마감임박·지연·위험) + 프로젝트 통합 검색 + 내 스페이스(도넛 진행률).
 *
 * 2026-08-18 — dev 육안 확인 완료(헤드리스 렌더 1600/1280px 검증)로 **기본 ON 승격**.
 * 승격 전에는 착지 화면을 통째로 바꾸는 변경이라 opt-in(`='1'`) 으로 두고 있었다.
 *   - ON  : 기본 — ProjectHomeSpaces 렌더.
 *   - OFF : 롤백 경로 — `VITE_PROJECT_HOME_AW001='0'` 이면 기존 3c 위젯 홈이
 *           손대지 않은 그대로 렌더된다(PROJECT_HOME_3C).
 *
 * 3c 위젯 홈 테스트는 ProjectPage.test.tsx 에서 `PROJECT_HOME_3C && !PROJECT_HOME_AW001`
 * 로 게이팅한다 — 롤백 경로를 되살렸을 때만 돈다.
 */
export const PROJECT_HOME_AW001 = import.meta.env.VITE_PROJECT_HOME_AW001 !== '0';

/**
 * ⭐⭐ **프로젝트 메뉴 v2 — `feature/dev-syk` 를 ★ «정본»으로 붓는 깃발**
 * (2026-10-08 사용자 결정: 「프로젝트 메뉴는 dev-syk 가 정본이다 · 하위 메뉴부터 기능 모두를
 * 만들어야 한다」 ⇒ 전략 ⓒ 「깃발 뒤에 통째로」).
 *
 * ## ⛔⛔ 이 깃발만 ★ 극성이 ⭐ «반대»다 — `=== '1'` 이다
 *
 * ```
 * 이 파일의 다른 넷    !== '0'   ⇒ ★ 기본값이 ⭐ «켜짐»
 *   TASK_PROJECT_SIDEPANEL · PROJECT_TEAMS_SHELL · PROJECT_HOME_3C · PROJECT_HOME_AW001
 * ⇒ ⛔ 그 관용구를 따르면 ⇒ ★ 운영에서 ⭐ «켜진 채 태어난다»
 *   (.env.production 에 프로젝트 줄은 ★ AW001=0 «하나»뿐이다 — 새 줄을 누가 챙길 수가 없다)
 * ⇒ ⇒ ★ 그것은 ⭐ 전략 ⓒ 의 ★ 정반대다. ⇒ 그래서 ⭐ `=== '1'`(기본 꺼짐) 로 둔다.
 * ```
 * ⇒ ⛔⛔ **다음 사람에게**: 이것을 ⭐ 「관용구에 맞춘다」며 `!== '0'` 으로 **고치지 마십시오.**
 *   ★ 고치면 ⇒ ⭐ 다 만들어지지도 않은 화면이 ★ 운영에 그대로 뜹니다.
 *   ⇒ ★ 켜는 것은 ⭐ 「하위 메뉴가 **전부** 돌고 ⇒ 사용자가 보고 **확인한** 뒤」다(아래 만료 조건).
 *
 * ## ⛔ 기존 넷을 ★ 재사용하지 «않는다»
 *
 * `PROJECT_HOME_AW001`·`PROJECT_HOME_3C` 는 ⭐ **「홈의 배치」**를 뜻한다(어느 홈을 그리나).
 * ⇒ ★ 이 깃발은 ⭐ **「메뉴 전체가 어느 정본에서 오나」**다 — ★ 뜻이 다르므로 섞지 않는다.
 *
 * ## ⭐ 「으뜸」이다 — ⚠ 다만 ★ 1차에서는 ⭐ «새 주소 둘»에만 있다
 *
 * ```
 * 켜지면 ⇒ ★ 기존 넷을 ⭐ 아예 안 본다(갈래가 ★ 둘이 된다)
 * ⚠ 그런데 ★ 1차(뼈대)는 ⇒ ⛔ 기존 파일을 «한 글자도» 안 건드린다(그것이 1차의 정의다)
 * ⇒ ⇒ ★ 그래서 1차의 분기는 ⭐ «새 주소 둘»에만 있다:
 *      project/checklist-templates · project/space/:teamId   ⇒ ProjectV2Route 한 곳
 * ⇒ ★ `/project` 를 ⭐ «넘겨받는» 것은 ★ 2차다 — 그때 이 깃발이 비로소 «으뜸»이 된다.
 * ```
 * ⚠ 그래서 지금 ★ 이 깃발을 «읽는» 제품 파일이 ⭐ 정확히 **둘**이다(이 선언 ＋ `ProjectV2Route`).
 *   ⇒ ★ 그 수를 ⭐ `project-v2-flag.guard.test.ts` 가 못 박는다 — 갈래가 ⛔ 번지지 않게.
 *
 * ## ⛔⛔ 만료 조건 — ★ 「켤 조건」과 「지울 조건」은 ⭐ «다른 날»이다
 *
 * ⚠ 까닭이 이 레포에 있다: `ProjectPage.tsx` 의 마지막 갈래가 ⭐ 「두 깃발이 **둘 다 0** 일 때만
 *   닿는다 ⇒ **산 길이 아니다**」 — ★ 끄는 조건을 안 적어 ⇒ ⛔ **죽은 갈래가 남은 것**이다.
 * ```
 * ⓐ 켤 조건   하위 메뉴가 ★ «전부» 돌고 ⇒ ⭐ 사용자가 보고 «확인»하면 (2026-10-08 결정 ⓐ＋ⓑ)
 *    재는 법   pnpm -C apps/frontend exec vitest run src/pages/ProjectV2 --reporter=basic
 *              ⇒ ★ 그 폴더의 시험이 ⭐ «0 실패»이고 ★ 하위 메뉴 표의 칸이 전부 ✅ 일 때
 * ⓑ 지울 조건  ⓐ 와 ★ «다른 날» — 켜 두고 한동안 지켜본 뒤 ⇒ ⭐ 옛 폴더를 지운다
 *    재는 법   git grep -l "pages/ProjectPage/" -- apps/frontend/src  ⇒ ★ 0건이 될 때
 *              (⇒ ⛔ 0건이 아니면 ★ 지우면 깨진다)
 * ```
 * ⇒ ⛔⛔ **막는 것은 ★ 「넷」이다 — 하나만 적으면 ⭐ 그것이 ✅ 될 때 ★ 나머지가 «함께 통과»한다:**
 * ```
 * 1. 서버 끝점        사용자 정의 필드 5 · 업무 승인 4 · 휴지통·복구 3   (관제 측정 2026-10-08)
 *    ⚠⚠ 2026-10-08 ⭐ «오후에 둘이 더 나왔다» — ⛔ 종전 「12개」는 ★ 모자란 수였다:
 *      ⭐ GET /api/v1/tasks/:id        ★ 단건 조회 — 목록은 설명·담당자·태그를 안 준다
 *      ⭐ serializeTask 의 칸 ★ 일곱   startDate·progress·requiresDoneApproval·approvedBy
 *                                     ·approvedAt·rejectReason·★ canApproveDone
 *    ⇒ 찾은 방법 ⇒ ★ 디자인이 4-4(업무 상세 패널) «앞»에서 재서 ⇒ ⛔ 멈췄다
 *    ⇒ ⇒ ★ 그러므로 ⭐ 「12/12 ✅」를 보고 ⇒ ⛔ 「서버는 끝났다」로 ★ 읽지 말 것.
 *    ⇒ ⇒ ⇒ ✅ ⭐ 그 둘은 ★ «같은 날» 채워졌다 — ⇒ ★ PR #2316 (2026-10-08 밤)
 *          ⇒ ⚠ 그래서 ★ 아래 ⭐ 재는 법은 이제 ⇒ ★ 「없다」를 찾는 것이 아니라
 *            ⇒ ⇒ ⭐ ★ «되돌아가지 않았나»를 재는 ★ 되돌림 가드다.
 *    ⇒ ⇒ ⇒ ⭐ 재는 법 ⇒ ★ 그 끝점·칸을 ⭐ «이름으로» 센다(⛔ 수로 세지 않는다):
 *        git grep -c "'/api/v1/tasks/:id'" origin/main -- apps/chat-server/src/modules/tasks/tasks.routes.ts
 *        ⇒ ★ ⭐ 3 이어야 한다 (GET·PATCH·DELETE) — ⇒ ⛔ 2 면 ★ GET 이 아직 없다
 * 2. 공유 셋 셋        router(+2줄) · project-i18n.ts(열쇠) · api/*.ts(새 함수) ⇒ ★ 셋 다 additive
 * 3. 사용자 확인       ⓐ 의 「보고 확인」
 * 4. 앱(iOS·AOS)      ✅ ★ 막는 것이 ⛔ «아니다» — dev-syk 가 바꾼 앱 파일 ⭐ AOS 0 · iOS 0 이고
 *                     앱이 쓰는 프로젝트 끝점 여섯은 ★ 2차가 additive 라 ⭐ 영향 0
 *                     ⇒ ⚠ 다만 ★ «뒤따를 일»이다 — 웹이 켜지면 ⭐ 앱과 화면이 달라진다(사용자 결정 사안)
 * 5. ⭐⭐ 운영 DB      ★ 마이그 ⭐ 219 의 표 다섯이 ⇒ ★ ⭐ «운영에» 있나   (2026-10-08 오후 추가)
 *                     ⇒ ⛔ 없으면 ★ 켜는 날 ⇒ ⭐ 그 끝점들이 ★ ⭐ «전부 500» 이다
 *                     ⇒ ⭐ 지시서 ⇒ ★ docs/ops/prod-check-migrations-20261008.md (⭐ 2판)
 *                     ⇒ ⚠ 왜 늦게 넣었나 ⇒ ★ 아침엔 ⭐ «부르는 코드가 0» 이라 ⇒ 막는 것이 아니었다
 *                        ⇒ ⇒ ★ 오후에 4-2 가 ⭐ /task-groups 를 «코드로» 부르자
 *                        ⇒ ⇒ ⇒ ⭐ chat-server 의 자동 경보가 ★ 빨개졌다(그 판이 ⭐ 뒤집었다)
 *                        ⇒ ⇒ ⇒ ⇒ ★ 아직 서 있는 신호 ⇒ ⭐ customFields ★ 쪽 하나
 *                           (task-groups-custom-fields.routes.test.ts — ⇒ ⭐ 그것이 ★ 이 관문을 지킨다)
 * 6. ⭐⭐ i18n 열쇠     ★ `pages/ProjectV2/custom-field-ui.tsx` 의 ⭐ 아홉이 ★ ⭐ «선언돼 있지 않다»
 *                     (2026-10-09 · ⇒ 인사 피어가 재서 찾음)
 *                     ⇒ ★ :26 `FIELD_TYPE_LABEL: Record<CustomFieldType, ⭐ string>` ＋ :204·:212·:228 `as never`
 *                     ⇒ ⇒ ⛔ ★ ⭐ «두 겹»으로 검사가 꺼져 있다 ⇒ ⇒ 없는 열쇠가 ★ tsc 를 ⭐ 통과한다
 *                     ⇒ ⇒ ⇒ ⛔ 그리고 `tTask:275 if (!m) return key;` ⇒ ⇒ ★ 화면에 ⭐ «열쇠 글자»가 나온다
 *                        (task.field.type_single · _multi · _date · _status · _text · _number
 *                         · _url · _checkbox · _member ⇒ ★ ⭐ 아홉 다 0건)
 *                     ⇒ ⭐ 닻 ⇒ ★ 같은 i18n 파일에 `task.field.*` 가 ⭐ 16개 ⇒ ⇒ ✅ 세는 법은 ★ 살아 있다
 *                     ⇒ ✅ 지금은 ★ ⭐ «도달 불가» — ⇒ ⭐ 인사 피어가 ★ «모듈»로 세어 ⭐ 확정했다:
 *                        ★ `custom-field-ui` 를 import 하는 ⭐ «제품» 파일 ⇒ ★ 0 (⇒ 3파일 중 시험 2 ＋ 주석 1)
 *                        ★ `pages/ProjectV2/index.ts` 의 ⭐ barrel re-export ⇒ ★ 0
 *                        ⇒ ⭐ 모듈로 세면 ★ 그 파일이 내놓는 ⭐ 이름 ★ 13 을 ⇒ ⭐ «한꺼번에» 덮는다
 *                           (⇒ ⛔ 관제가 ★ 이름 «둘»만 세서 ⇒ ⭐ 좁았다)
 *                        ★ [닻] 같은 폴더 이웃 ⇒ task-cell-popovers 2 · FieldNameEditor 3 · task-i18n 25 파일
 *                           ⇒ ⇒ ✅ 그러므로 ★ 이 「0」은 ⭐ «측정 실패»가 아니다
 *                        ⇒ ⚠ 그런데 ★ 디자인의 4-4 가 ⇒ ⭐ FieldCell 을 ★ «처음 부르는» 판이다
 *                           ⇒ ⛔ 그것은 ★ ⭐ «측정이 아니라 설계 의도»다 — ⇒ 근거는 그 사슬을 적은 주석
 *                             (custom-fields.guard.test.ts:17 — `MyTaskDetailPanel (4-4) ─→ custom-field-ui ─→ …`)
 *                     ⇒ ⭐⭐ 켜는 날의 ★ 트리거가 ⇒ ⭐ «이름으로» 적혀 있다:
 *                        docs/ops/prod-deploy-35-20261009.md:39 ⇒ 「★ 켜기 전에 ⭐ 마이그 219 확인이 남아 있다」
 *                        ⇒ ⇒ ★ 즉 ⇒ ⭐ ★ «마이그 219 확인 ⇒ 그다음이 ⭐ 켜는 날»이다(⛔ 「언젠가」가 아니다)
 *                     ⇒ ⭐ 고침은 ★ 둘이다(⛔ 하나만 하면 ⭐ 다시 꺼진다):
 *                        ① i18n 에 아홉 추가  ② `Record<…, ⭐ TaskI18nKey>` 로 바꾸고 `as never` 떼기
 *                           (⇒ ★ 그 이름은 ⭐ `task-i18n.ts:266` 이 내놓는다 — ⛔ `I18nKey` 가 ★ 아니다)
 *                           ⇒ ✅ 그리고 ★ `as never` 를 떼도 ⇒ ⭐ 다른 오류가 ★ 안 생긴다(인사 피어가 타입을 따라갔다 —
 *                              :204 `t` 와 :228 `picked` 가 ★ 둘 다 ⭐ CustomFieldType 으로 좁혀져 ★ 색인이 «총»이다)
 *                        ⇒ RED ⇒ ★ ⭐ «①②를 한 뒤» ⇒ ★ 아홉 중 하나를 지우면 ⇒ ⭐ «tsc 가 빨개져야» 한다
 *                           (⇒ ⛔ 지금은 ★ 그 아홉이 ⭐ «없어서» ★ 지울 것이 없다)
 * ```
 * ⚠⚠ **이 목록이 ★ 세 번 모자랐다** — ⇒ ⭐ 1 은 수가 적었고 · ★ 5 는 ⭐ 아예 없었고 · ★ 6 도 ⭐ 없었다.
 *   ⇒ ★ 까닭은 셋 다 ⭐ 같다: ⇒ ★ 적을 때 ⭐ «막지 않던 것»이 ⇒ ★ 뒤에 ⭐ «막는 것»이 되었다.
 *   ⇒ ⇒ ⭐ 그러므로 ★ 이 목록은 ⇒ ⭐ 「다 적었다」가 아니라 ★ ⭐ «그날까지 아는 것»이다.
 *   ⇒ ⇒ ⇒ ★ 켜기 «전»에 ⇒ ⭐ 이 목록을 ★ ⭐ «다시 세어라» — ⇒ ⛔ 읽고 믿지 말 것.
 *   ⇒ ⇒ ⇒ ⇒ ⭐⭐ ★ 그리고 ⇒ ⭐ 6 은 ★ 셋 중 ⭐ «처음으로» ⇒ ★ 이 경고 «덕에» 들어온 것이 ⛔ 아니다 —
 *      ⇒ ★ 다른 세션이 ⇒ ⭐ ★ «딴 일(as never 전수)»을 하다 ⇒ ★ 걸려서 왔다.
 *      ⇒ ⇒ ⚠ 즉 ★ 이 경고는 ⇒ ⭐ ★ «스스로 세지 않는다». ⇒ ⇒ ★ 켜는 사람이 ⭐ 세야 한다.
 */
/*
 * ⭐⭐ 2026-10-12 사용자 결정 — ★ 이 깃발을 ⭐ «걷지 않는다».
 *
 * ## ⚠ 물음이 무엇이었나
 *
 * 깃발은 이미 켜져 있고(2026-10-09) 제품 파일 둘만 읽으니 ⇒ 「걷어서(항상 켜짐으로 만들어)
 * 깃발을 없애자」는 제안이 나왔다. 사용자 눈에 보이는 변화는 0 이었다.
 *
 * ## ⛔ 걷지 않는 까닭 — ★ 그것이 «되돌아갈 길»이기 때문이다
 *
 * ```
 * 라우터가 이 깃발의 문(ProjectV2Route)에 ★ fallback 을 «주는 그 한 곳» —
 *   그 fallback 이 ⇒ ⭐ 1차 화면(MyTasksDetailPage)으로 돌아가는 길이다.
 * ⇒ ⇒ 깃발을 걷으면 ★ 그 길이 «소스에서 사라진다» ⇒ 되돌리기가
 *   ⇒ 「.env 한 줄 ＋ 배포 한 차례」  에서
 *   ⇒ ⭐ 「코드를 다시 짜고 ＋ 리뷰 ＋ 배포」로 바뀐다.
 * ⇒ ⇒ ⇒ ★ 그 둘이 치르는 값(깃발 하나를 남기는 값)이 ⇒ ⭐ 그 길을 잃는 값보다 «작다».
 * ```
 *
 * ⚠ 줄 번호를 적지 «않는다» — 남이 줄을 더하면 썩는다. 「router 가 fallback 으로 주는 그 한 곳」
 *   으로 찾아라. 그리고 이 깃발을 읽는 제품 파일은 ★ 위 머리말이 적어 둔 «그 둘»이다
 *   (새 수를 여기 더하지 않는다 — 수는 스스로 썩고, 가드가 이미 그것을 잠그고 있다).
 *
 * ## ⏳ 만료 조건 — ★ «이름»으로 적는다
 *
 * ```
 * 「1차 화면(MyTasksDetailPage)을 더 이상 내놓지 않는 날」 ⇒ ⭐ 이 깃발도 «함께» 걷는다.
 * ⇒ 까닭 ⇒ 그날이 ★ 이 깃발이 지키던 «되돌아갈 길»이 사라지는 날이고,
 *   ⇒ ⇒ 그러면 깃발을 남길 값이 ⭐ 0 이 된다.
 * ⚠ 파일 이름이 아니라 ★ 「그 이름을 내놓지 않는다」로 적은 까닭 ⇒ 남이 같은 이름을
 *   «일부만» 옮겨도 뜻이 안 깨지게.
 * ```
 */
export const PROJECT_V2 = import.meta.env.VITE_PROJECT_V2 === '1';
